/**
 * Mock store for integration testing purposes.  Implements reducers for the actions which can be dispatched in isolation (outside of the web app itself).
 */
import { configureStore } from "@reduxjs/toolkit";
import { storylineReducer } from "./storyline";
import { notificationsReducer } from "./notifications";
import { appReducer } from "./app";
import { profilingReducer } from "../../src/store/profiling/reducers";
import { tracingReducer } from "../../src/store/tracing/reducers";

export type StubStoreState = {
    parameterValues?: Record<string, any>;
    canvasState?: Record<string, any>;
    frameData?: Record<string, any>;
    templates?: Record<string, { jsx: string, css?: string }>;
    storylineId?: string;
    datasourceValues?: Record<string, any>;
    snapshot?: Record<string, any>;
}

export const initializeStubStore = (initialState: StubStoreState) => configureStore({
    reducer: {
        storyline: storylineReducer,
        notifications: notificationsReducer,
        // Canvas / Template call useProfilingSink, which reads s.profiling; the reducer's own
        // initialState (profiling disabled) is sufficient for tests.
        profiling: profilingReducer,
        // storyline thunks dispatch addTraceEvent, which reads s.tracing.enabled (disabled by default).
        tracing: tracingReducer,
        // Canvas selects s.app.activeBranch and NotificationBanner reads s.app.banner...
        app: appReducer,
    },
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false }),
    preloadedState: {
        notifications: {
            notifications: [],
            outstandingRequestCount: 0,
        },
        storyline: {
            id: initialState.storylineId ?? "",
            parameterValues: new Map(Object.entries(initialState.parameterValues ?? {})),
            canvasState: initialState.canvasState ?? {},
            currentParagraph: {
                data: initialState.frameData ?? {},
            },
            datasources: new Map<string, any>(),
            datasourceValues: new Map(Object.entries(initialState.datasourceValues ?? {})),
            datasourcesInFlight: new Set<string>(),
            chapterIndex: 0,
            pageIndex: 0,
            paragraphIndex: 0,
            loading: false,
            snapshot: initialState.snapshot ?? null,
            storyline: {
                name: initialState.storylineId ?? "",
                templates: initialState.templates ?? {},
            },
        },
    },
});